/**
 * Admin Page (admin-only)
 *
 * User management for admins. Search users and grant or revoke a "free pass" —
 * comped app access until a chosen date. Visible only to admins (gated
 * server-side by ADMIN_EMAILS and in the dashboard nav by user.is_admin).
 */

import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Search, UserCog, Check, X } from 'lucide-react';
import { useAdminUsers, useSetFreePass, type AdminUser } from '@/hooks/queries';
import { getErrorMessage } from '@/integrations/api/client';
import { LoadingSpinner } from '@/components/shared';

function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const isPassActive = (until: string | null): boolean =>
  !!until && new Date(until).getTime() > Date.now();

/** Today + n months, formatted as yyyy-mm-dd for the date input default. */
function defaultUntil(): string {
  const d = new Date();
  d.setMonth(d.getMonth() + 3);
  return d.toISOString().slice(0, 10);
}

function StatusBadge({ user }: { user: AdminUser }) {
  const active = isPassActive(user.free_pass_until);
  if (active) {
    return (
      <span
        className="px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide"
        style={{ background: '#E8F6F4', color: '#2A9D96' }}
      >
        Free pass · until {formatDate(user.free_pass_until)}
      </span>
    );
  }
  const paid = user.subscription_status === 'active' || user.subscription_status === 'trialing';
  return (
    <span
      className="px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide"
      style={{
        background: paid ? '#FDF6E8' : '#F2F1EE',
        color: paid ? '#B8860B' : '#9B978E',
      }}
    >
      {user.subscription_status}
    </span>
  );
}

function UserRow({ user }: { user: AdminUser }) {
  const [until, setUntil] = useState(defaultUntil());
  const setFreePass = useSetFreePass();
  const active = isPassActive(user.free_pass_until);

  const grant = () => {
    if (!until) return;
    setFreePass.mutate(
      { userId: user.id, input: { until: new Date(until).toISOString() } },
      {
        onSuccess: () => toast.success(`Free pass granted to ${user.email}`),
        onError: (err) => toast.error(getErrorMessage(err)),
      }
    );
  };

  const revoke = () => {
    setFreePass.mutate(
      { userId: user.id, input: { until: null } },
      {
        onSuccess: () => toast.success(`Free pass revoked for ${user.email}`),
        onError: (err) => toast.error(getErrorMessage(err)),
      }
    );
  };

  return (
    <div
      className="rounded-xl border p-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between"
      style={{ background: '#FFFFFF', borderColor: '#E8E6E1' }}
    >
      <div className="min-w-0">
        <div className="text-[14.5px] font-medium truncate" style={{ color: '#2A2926' }}>
          {user.email}
        </div>
        <div className="mt-1 flex items-center gap-2 flex-wrap">
          <StatusBadge user={user} />
          <span className="text-[12px]" style={{ color: '#9B978E' }}>
            joined {formatDate(user.created_at)}
          </span>
        </div>
        {active && user.free_pass_note && (
          <div className="mt-1 text-[12px]" style={{ color: '#9B978E' }}>
            note: {user.free_pass_note}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <input
          type="date"
          value={until}
          onChange={(e) => setUntil(e.target.value)}
          className="px-3 py-2 rounded-lg border text-[13px] outline-none"
          style={{ borderColor: '#E8E6E1', color: '#2A2926' }}
        />
        <button
          onClick={grant}
          disabled={setFreePass.isPending || !until}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[13px] font-semibold text-white transition disabled:opacity-50"
          style={{ background: '#3ECDC6' }}
        >
          <Check className="h-4 w-4" />
          {active ? 'Update' : 'Grant'}
        </button>
        {active && (
          <button
            onClick={revoke}
            disabled={setFreePass.isPending}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[13px] font-semibold transition disabled:opacity-50"
            style={{ background: '#FFFFFF', color: '#C0524B', border: '1px solid #E8C9C5' }}
          >
            <X className="h-4 w-4" />
            Revoke
          </button>
        )}
      </div>
    </div>
  );
}

export function AdminPage() {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search, 300);
  const { data: users = [], isLoading, isError } = useAdminUsers(debouncedSearch);

  return (
    <div>
      <div className="flex items-center gap-3 mb-1">
        <UserCog className="h-6 w-6" style={{ color: '#3ECDC6' }} />
        <h1 className="text-2xl font-semibold" style={{ color: '#2A2926' }}>Admin · Users</h1>
      </div>
      <p className="text-[14px] mb-6" style={{ color: '#6B675E' }}>
        Grant a free pass to comp a user free access until a chosen date, or revoke it.
      </p>

      {/* Search */}
      <div className="relative mb-5 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: '#9B978E' }} />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by email…"
          className="w-full pl-10 pr-4 py-2.5 rounded-full border text-[14px] outline-none"
          style={{ borderColor: '#E8E6E1', color: '#2A2926' }}
        />
      </div>

      {isLoading ? (
        <div className="py-16 flex justify-center"><LoadingSpinner /></div>
      ) : isError ? (
        <div className="py-16 text-center text-[14px]" style={{ color: '#C0524B' }}>
          Could not load users. Please try again.
        </div>
      ) : users.length === 0 ? (
        <div className="py-16 text-center text-[14px]" style={{ color: '#9B978E' }}>
          No users found.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {users.map((u) => (
            <UserRow key={u.id} user={u} />
          ))}
        </div>
      )}
    </div>
  );
}
